import { createBrowserRouter } from 'react-router';
import { ROUTE_PATHS } from '@/lib';
import { RouterProviders } from '@/app/providers';
import { MainLayout, FullscreenLayout, HomeLayout } from '@/app/layouts';
import { ProtectedRoute, PublicOnlyRoute, RecoveryRoute } from './guards';

// ═════════════════════════════════════════════════════════════════════════
// EAGER-загруженные страницы — в initial bundle, без lazy.
// ═════════════════════════════════════════════════════════════════════════
// Юзер при первом заходе всегда попадает либо на /welcome (если не залогинен),
// либо на /feed (если залогинен). Lazy-ить эти две страницы = лишний round-trip
// перед первым рендером, что на flaky-mobile сети даёт заметный белый экран.
// Все остальные 28 страниц — lazy (см. ./lazyPages.ts).
import { WelcomePage } from '@/pages/welcome';
import { FeedPage } from '@/pages/home-feed';

// ═════════════════════════════════════════════════════════════════════════
// LAZY-страницы — вынесены в отдельный файл для корректного HMR.
// ═════════════════════════════════════════════════════════════════════════
// Причина: ESLint правило react-refresh/only-export-components требует чтобы
// файл экспортил только компоненты или только не-компоненты. Здесь экспортится
// `router` (объект), поэтому lazy-компоненты живут в ./lazyPages.ts.
import {
  SignInPage,
  SignUpPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  SelectionsPage,
  PopularPage,
  HomeWishlistsPage,
  WishlistPage,
  CommunityPage,
  UserProfilePage,
  WishlistDetailPage,
  WishlistItemDetailPage,
  NotFoundPage,
  DebugColorsPage,
  EditProfilePage,
  SettingsPage,
  NotificationsPage,
  SettingsLanguagePage,
  SettingsCurrencyPage,
  ChangePasswordPage,
  AppNotificationsPage,
  BlockedUsersPage,
  TermsPage,
  PrivacyPolicyPage,
  SupportPage,
  WishlistFormPage,
  WishlistItemFormPage,
  FriendProfilePage,
  FriendWishlistsPage,
  MyItemsPage,
  UserItemsPage,
  SecretSantaFormPage,
  SecretSantaDetailPage,
  SecretSantaListPage,
  BookingsListPage,
  SelectionDetailPage,
  SelectionItemDetailPage,
} from './lazyPages';

/**
 * Единый роутер приложения.
 *
 * Архитектура:
 * - Публичные auth-страницы (welcome, sign-in, sign-up, forgot-password)
 *   защищены PublicOnlyRoute — если юзер уже залогинен, редиректит на /
 * - /reset-password защищён RecoveryRoute — доступен всем (для обработки
 *   ссылки из email)
 * - Все остальные (приватные) страницы обёрнуты в ProtectedRoute —
 *   если юзер не авторизован, редиректит на /sign-in с сохранением
 *   исходного пути в location.state.from
 *
 * URL всегда синхронизирован с состоянием приложения благодаря тому что
 * это один роутер, а не два переключающихся.
 *
 * Code splitting:
 * - WelcomePage + FeedPage — eager (первый экран для обеих веток авторизации)
 * - Layouts / guards / providers — eager (нужны на всех страницах)
 * - Остальные 28 страниц — lazy (каждая в своём chunk'е)
 * - Suspense fallback — в App.tsx, покрывает весь RouterProvider
 */
export const appRouter = createBrowserRouter([
  {
    element: <RouterProviders />,
    children: [
      // ═══ Публичные auth-страницы ═══
      // Залогиненных юзеров отсюда редиректит на /feed через PublicOnlyRoute
      {
        element: <PublicOnlyRoute />,
        children: [
          {
            element: <FullscreenLayout />,
            children: [
              {
                path: ROUTE_PATHS.WELCOME,
                element: <WelcomePage />,
              },
              {
                path: ROUTE_PATHS.SIGN_IN,
                element: <SignInPage />,
              },
              {
                path: ROUTE_PATHS.SIGN_UP,
                element: <SignUpPage />,
              },
              {
                path: ROUTE_PATHS.FORGOT_PASSWORD,
                element: <ForgotPasswordPage />,
              },
            ],
          },
        ],
      },

      // ═══ Recovery-страница (доступна всем) ═══
      // Обрабатывает ссылку из email для сброса пароля
      {
        element: <RecoveryRoute />,
        children: [
          {
            element: <FullscreenLayout />,
            children: [
              {
                path: ROUTE_PATHS.RESET_PASSWORD,
                element: <ResetPasswordPage />,
              },
            ],
          },
        ],
      },

      // ═══ Приватные страницы (требуют авторизации) ═══
      {
        element: <ProtectedRoute />,
        children: [
          // Страницы с Bottom Menu (MainLayout)
          {
            element: <MainLayout />,
            children: [
          // Home routes — вложены в HomeLayout (header + табы)
          {
            element: <HomeLayout />,
            children: [
              {
                path: ROUTE_PATHS.HOME_FEED,
                element: <FeedPage />,
              },
              {
                path: ROUTE_PATHS.HOME_SELECTIONS,
                element: <SelectionsPage />,
              },
              {
                path: ROUTE_PATHS.HOME_POPULAR,
                element: <PopularPage />,
              },
              {
                path: ROUTE_PATHS.HOME_WISHLISTS,
                element: <HomeWishlistsPage />,
              },
            ],
          },

          // My Wishlists - с меню
          {
            path: ROUTE_PATHS.MY_WISHLISTS,
            element: <WishlistPage />,
          },

          // Community - с меню
          {
            path: ROUTE_PATHS.COMMUNITY,
            element: <CommunityPage />,
          },

          // Profile - с меню
          {
            path: ROUTE_PATHS.PROFILE,
            element: <UserProfilePage />,
          },

          // Wishlist детали - теперь С меню
          {
            path: ROUTE_PATHS.WISHLIST_DETAIL,
            element: <WishlistDetailPage />,
          },

          // Профиль другого пользователя - С меню
          {
            path: ROUTE_PATHS.USER_PROFILE,
            element: <FriendProfilePage />,
          },

          // Все желания текущего пользователя - С меню
          {
            path: ROUTE_PATHS.MY_ITEMS,
            element: <MyItemsPage />,
          },

          // Все желания другого пользователя - С меню
          {
            path: ROUTE_PATHS.USER_ITEMS,
            element: <UserItemsPage />,
          },

          // Secret Santa список - С меню
          {
            path: ROUTE_PATHS.SECRET_SANTA_LIST,
            element: <SecretSantaListPage />,
          },

          // Подборки - С меню
          {
            path: ROUTE_PATHS.SELECTION_DETAIL,
            element: <SelectionDetailPage />,
          },
        ],
      },

      // Страницы БЕЗ Bottom Menu (FullscreenLayout)
      {
        element: <FullscreenLayout />,
        children: [
          // Item детали - без меню, свой footer с кнопками действий
          {
            path: ROUTE_PATHS.ITEM_DETAIL,
            element: <WishlistItemDetailPage />,
          },

          // Wishlist формы
          {
            path: ROUTE_PATHS.WISHLIST_NEW,
            element: <WishlistFormPage />,
          },
          {
            path: ROUTE_PATHS.WISHLIST_EDIT,
            element: <WishlistFormPage />,
          },

          // Item формы
          {
            path: ROUTE_PATHS.ITEM_NEW,
            element: <WishlistItemFormPage />,
          },
          {
            path: ROUTE_PATHS.ITEM_NEW_GENERIC,
            element: <WishlistItemFormPage />,
          },
          {
            path: ROUTE_PATHS.ITEM_NEW_PREFILLED,
            element: <WishlistItemFormPage />,
          },
          {
            path: ROUTE_PATHS.ITEM_EDIT,
            element: <WishlistItemFormPage />,
          },

          // User profile и wishlists
          {
            path: ROUTE_PATHS.USER_WISHLISTS,
            element: <FriendWishlistsPage />,
          },

          // Profile edit и settings
          {
            path: ROUTE_PATHS.PROFILE_EDIT,
            element: <EditProfilePage />,
          },
          {
            path: ROUTE_PATHS.SETTINGS,
            element: <SettingsPage />,
          },
          {
            path: ROUTE_PATHS.CHANGE_PASSWORD,
            element: <ChangePasswordPage />,
          },
          {
            path: ROUTE_PATHS.NOTIFICATIONS,
            element: <NotificationsPage />,
          },

          // Новые страницы настроек
          {
            path: ROUTE_PATHS.SETTINGS_LANGUAGE,
            element: <SettingsLanguagePage />,
          },
          {
            path: ROUTE_PATHS.SETTINGS_CURRENCY,
            element: <SettingsCurrencyPage />,
          },
          {
            path: ROUTE_PATHS.APP_NOTIFICATIONS,
            element: <AppNotificationsPage />,
          },
          {
            path: ROUTE_PATHS.BLOCKED_USERS,
            element: <BlockedUsersPage />,
          },
          {
            path: ROUTE_PATHS.TERMS,
            element: <TermsPage />,
          },
          {
            path: ROUTE_PATHS.PRIVACY_POLICY,
            element: <PrivacyPolicyPage />,
          },
          {
            path: ROUTE_PATHS.SUPPORT,
            element: <SupportPage />,
          },

          // Secret Santa routes
          {
            path: ROUTE_PATHS.SECRET_SANTA_NEW,
            element: <SecretSantaFormPage />,
          },
          {
            path: ROUTE_PATHS.SECRET_SANTA_DETAIL,
            element: <SecretSantaDetailPage />,
          },
          {
            path: ROUTE_PATHS.SECRET_SANTA_EDIT,
            element: <SecretSantaFormPage />,
          },

          // Bookings routes
          {
            path: ROUTE_PATHS.BOOKINGS_LIST,
            element: <BookingsListPage />,
          },

          // Детали товара подборки - без меню, свой sticky footer
          {
            path: ROUTE_PATHS.SELECTION_ITEM_DETAIL,
            element: <SelectionItemDetailPage />,
          },

          // Debug — временная страница цветов
          {
            path: ROUTE_PATHS.DEBUG_COLORS,
            element: <DebugColorsPage />,
          },

          // Fallback для неизвестных маршрутов — показываем 404 страницу
          // с понятным сообщением вместо молчаливого редиректа на главную.
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
        ],
      },
    ],
  },
]);
